import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import _ from 'lodash'
import { getModelUrl, getVehiclesYears } from 'tw-oi-core/utils/vehicle'
import {
  setVehicleProgram,
  getVehicleProgram,
  trackChangeVehicleClick,
  setYearDimension,
  setTypeSubType,
  setBrand,
} from 'tw-oi-core/services/analytics'

import UnifiedHeader from '../components/UnifiedHeader'
import VehicleYearSelector from '../components/VehicleYearSelector'
import VehicleList from '../components/VehicleList'
import VehicleModelSearch from '../components/VehicleModelSearch'
import BrandPillFilter from '../components/BrandPillFilter'
import Loader from '../components/Loader'
import StickyBar from '../components/StickyBar'
import Media from '../components/Media'
import * as VehicleActions from 'tw-oi-core/actions/VehicleActions'
import * as ContentsActions from 'tw-oi-core/actions/ContentsActions'
import {
  filterVehiclesByBrand,
  searchVehiclesByQuery,
  getUniqueBrandsFromVehicles,
} from '../extras/utils'
import { resolveAllOfflineVehicles } from '../extras/offlineVehicles'
import { navigateUnifiedHeaderLogo, buildStaticMobileDropdown } from '../extras/unifiedHeaderNavigation'
import { isOffline } from '../services/offlineDetect'
import { buildOfflineHeroShotMap, revokeOfflineHeroShotMap } from '../services/offlineHeroShot'
import {
  BRANDS,
  PRODUCT_MAP,
  PRODUCT_SUBTYPE_MAP,
  ROUTE,
  UI,
} from '../config'

import '../styles/LandingPage.scss'
import '../styles/DivisionVehicles.scss'
import '../styles/Vehicles.scss'

class Offline extends Component {
  static propTypes = {
    vehicle: PropTypes.object.isRequired,
    VehicleActions: PropTypes.object.isRequired,
    ContentsActions: PropTypes.object.isRequired,
    heroShotMap: PropTypes.object,
    isDesktop: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  }

  state = {
    allModels: [],
    years: [],
    currentYear: '',
    brandFilter: null,
    searchQuery: '',
    offlineHeroShotMap: {},
    ready: false,
  }

  componentDidMount() {
    this.loadModels()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isDesktop !== this.props.isDesktop && this.state.ready) {
      this.loadOfflineHeroShotMap()
    }
  }

  componentWillUnmount() {
    revokeOfflineHeroShotMap(this.state.offlineHeroShotMap)
  }

  loadOfflineHeroShotMap = async() => {
    const imageSize = this.props.isDesktop ? UI.HERO_SHOT_MEDIUM_SIZE : UI.HERO_SHOT_SMALL_SIZE
    const offlineHeroShotMap = await buildOfflineHeroShotMap(imageSize)
    this.setState(prevState => {
      revokeOfflineHeroShotMap(prevState.offlineHeroShotMap)
      return { offlineHeroShotMap }
    })
  }

  loadModels = async() => {
    const { vehicles, currentYear } = this.props.vehicle
    const { vehicles: offlineVehicles } = await resolveAllOfflineVehicles(vehicles)
    const years = getVehiclesYears(offlineVehicles).filter(y => y)
    const activeYear = years.includes(currentYear) ? currentYear : years[0] || ''
    const imageSize = this.props.isDesktop ? UI.HERO_SHOT_MEDIUM_SIZE : UI.HERO_SHOT_SMALL_SIZE
    const offlineHeroShotMap = await buildOfflineHeroShotMap(imageSize)

    if (activeYear) {
      this.props.VehicleActions.setVehicleYear(activeYear)
    }

    this.setState({
      allModels: offlineVehicles,
      years,
      currentYear: activeYear,
      offlineHeroShotMap,
      ready: true,
    })
  }

  handleYearSelected = year => {
    this.props.VehicleActions.setVehicleYear(year)
    this.setState({ currentYear: year, brandFilter: null, searchQuery: '' })
  }

  getModelsForYear() {
    const { allModels, currentYear, brandFilter, searchQuery } = this.state
    let models = allModels.filter(vehicle => vehicle.year === currentYear)

    models = filterVehiclesByBrand(models, brandFilter)
    models = searchVehiclesByQuery(models, searchQuery)

    return models.sort((a, b) => a.model.localeCompare(b.model))
  }

  getModelLink = vehicle => {
    const brand = BRANDS.find(item => item.name === vehicle.make)
    if (!brand) return ROUTE.INDEX

    return `/${brand.key}${ROUTE.GUIDE}${getModelUrl(vehicle.year, `${vehicle.id} ${vehicle.model}`)}${ROUTE.BROWSE}`
  }

  setVehicleState = vehicle => {
    const {
      setVehicleBrand,
      setVehicleId,
      setVehicleModel,
      setVehicleYear,
      setVehicleProductType,
      setVehicleProductSubtype,
    } = this.props.VehicleActions

    setVehicleBrand(vehicle.make)
    setVehicleId(vehicle.id)
    setVehicleModel(vehicle.model)
    setVehicleYear(String(vehicle.year))
    setVehicleProductType(vehicle.FOREST_RIVER_PRODUCT_TYPE)
    setVehicleProductSubtype(vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE)
  }

  navigateToVehicle = vehicle => {
    this.setVehicleState(vehicle)
    this.updateAnalyticsContext(vehicle)
    this.props.history.push(this.getModelLink(vehicle))
  }

  updateAnalyticsContext(vehicle) {
    const { model, make, year, FOREST_RIVER_PRODUCT_TYPE, FOREST_RIVER_PRODUCT_SUB_TYPE } = vehicle
    const vehicleProgramStringBefore = getVehicleProgram()
    const vehicleProgramStringAfter = `${year} ${make} ${model}`

    setVehicleProgram(year, make, model)
    setYearDimension(year)
    setBrand(make)

    const selectedProductType = PRODUCT_MAP[FOREST_RIVER_PRODUCT_TYPE]
      ? PRODUCT_MAP[FOREST_RIVER_PRODUCT_TYPE].title
      : FOREST_RIVER_PRODUCT_TYPE
    const selectedProductSubtype =
      PRODUCT_SUBTYPE_MAP[FOREST_RIVER_PRODUCT_SUB_TYPE] || FOREST_RIVER_PRODUCT_SUB_TYPE
    setTypeSubType(selectedProductType, selectedProductSubtype)
    trackChangeVehicleClick(vehicleProgramStringBefore, vehicleProgramStringAfter)
  }

  renderBreadcrumb() {
    return <span className="DivisionVehicles-breadcrumb-current">{UI.OFFLINE_PAGE_TITLE}</span>
  }

  handleLogoClick = () => {
    if (isOffline()) return
    navigateUnifiedHeaderLogo(this.props.history, false)
  }

  render() {
    const { isDesktop, heroShotMap } = this.props
    const { getHeroShot } = this.props.ContentsActions
    const { ready, years, currentYear, brandFilter, allModels, offlineHeroShotMap } = this.state
    const mergedHeroShotMap = { ...heroShotMap, ...offlineHeroShotMap }
    const displayModels = this.getModelsForYear()
    const availableBrands = getUniqueBrandsFromVehicles(
      allModels.filter(vehicle => vehicle.year === currentYear)
    )
    const productType = displayModels[0]?.FOREST_RIVER_PRODUCT_TYPE || 'MOTORHOME'

    if (!ready) {
      return (
        <div className="DivisionVehicles theme-forest-river Offline">
          <Loader className="inverse" />
        </div>
      )
    }

    return (
      <div className="DivisionVehicles theme-forest-river Offline">
        <Helmet>
          <title>{`${UI.OFFLINE_PAGE_TITLE} | ${UI.UNIFIED_APP_TITLE}`}</title>
        </Helmet>

        <UnifiedHeader
          breadcrumb={this.renderBreadcrumb()}
          mobileDropdown={buildStaticMobileDropdown(UI.OFFLINE_PAGE_TITLE)}
          onLogoClick={isOffline() ? undefined : this.handleLogoClick}
        />

        <Media type="desktop">
          <StickyBar className="sticky-vehicle-year-selector DivisionVehicles-yearBar">
            <VehicleYearSelector
              currentYear={currentYear}
              years={years}
              onSelected={this.handleYearSelected}
              slider={!isDesktop && years.length > 4}
              baseRoute={ROUTE.OFFLINE}
              variant="unified"
              preventNavigation
            />
          </StickyBar>
        </Media>
        <Media type="mobile">
          <VehicleYearSelector
            currentYear={currentYear}
            years={years}
            onSelected={this.handleYearSelected}
            slider={!isDesktop && years.length > 4}
            baseRoute={ROUTE.OFFLINE}
            variant="unified"
            preventNavigation
          />
        </Media>

        <main className="DivisionVehicles-content">
          <div className="DivisionVehicles-filters">
            <VehicleModelSearch
              vehicles={allModels.filter(vehicle => vehicle.year === currentYear)}
              onSelect={this.navigateToVehicle}
              label={UI.UNIFIED_SEARCH_STEP2_LABEL}
              placeholder={UI.UNIFIED_SEARCH_STEP1_PLACEHOLDER}
              filterList
              onQueryChange={query => this.setState({ searchQuery: query })}
            />
            <BrandPillFilter
              label={UI.UNIFIED_FILTER_BY_BRAND}
              allLabel={UI.UNIFIED_FILTER_ALL_BRANDS}
              brands={availableBrands}
              currentBrand={brandFilter}
              onChange={brand => this.setState({ brandFilter: brand })}
            />
          </div>

          {!_.isEmpty(displayModels) ? (
            <VehicleList
              isDesktop={isDesktop}
              currentYear={currentYear}
              productType={productType}
              currentModel={this.props.vehicle.currentModel}
              currentBrand={this.props.vehicle.currentBrand}
              vehicles={displayModels}
              groups={[]}
              heroShotMap={mergedHeroShotMap}
              getHeroShot={getHeroShot}
              setVehicleModel={vehicle => {
                this.setVehicleState(vehicle)
                this.updateAnalyticsContext(vehicle)
              }}
              baseRoute={ROUTE.OFFLINE}
              getModelLink={this.getModelLink}
              showBrandSubtitle
              hideSubtypeFilter
              listVariant="unified"
            />
          ) : null}
        </main>
      </div>
    )
  }
}

function mapStateToProps({ vehicle, contents, user }) {
  const media = user && user.media

  return {
    vehicle,
    isDesktop: media ? media.isDesktop : true,
    heroShotMap: contents.heroShotMap,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    VehicleActions: bindActionCreators(VehicleActions, dispatch),
    ContentsActions: bindActionCreators(ContentsActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Offline))
